import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useExtracted } from "next-intl";
import {
  savedStrategies as api,
  DEFAULT_SAVED_STRATEGY_LIMITS,
  type SavedStrategyInput,
  type SavedStrategyLimits,
} from "@/lib/api/saved-strategies";
import { ApiError } from "@/lib/api/transport";
import type { SavedStrategy } from "@/types/api";
import { useAuth } from "./useAuth";

export type SavedStrategiesStatus = "idle" | "loading" | "ready" | "error";

export interface SavedStrategiesContextValue {
  status: SavedStrategiesStatus;
  strategies: readonly SavedStrategy[];
  limits: SavedStrategyLimits;
  error: Error | null;
  reload: () => Promise<void>;
  create: (input: SavedStrategyInput) => Promise<SavedStrategy>;
  update: (
    id: string,
    patch: Partial<SavedStrategyInput>,
  ) => Promise<SavedStrategy>;
  remove: (id: string) => Promise<void>;
}

const SavedStrategiesContext = createContext<SavedStrategiesContextValue>(
  null!,
);

interface SavedStrategyQuotaErrorBody {
  error?: string;
  scope?: "ticker" | "total";
  limit?: number;
  count?: number;
  ticker?: string | null;
}

export class SavedStrategyLimitError extends Error {
  override name = "SavedStrategyLimitError" as const;

  constructor(
    message: string,
    readonly scope: "ticker" | "total",
    readonly limit: number,
    readonly ticker: string | null,
  ) {
    super(message);
  }
}

export function useSavedStrategies(): SavedStrategiesContextValue {
  return useContext(SavedStrategiesContext);
}

function useSavedStrategiesProvider(): SavedStrategiesContextValue {
  const te = useExtracted();
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<SavedStrategiesStatus>("idle");
  const [strategies, setStrategies] = useState<readonly SavedStrategy[]>([]);
  const [limits, setLimits] = useState<SavedStrategyLimits>(
    DEFAULT_SAVED_STRATEGY_LIMITS,
  );
  const [error, setError] = useState<Error | null>(null);
  const limitError = useCallback(
    (scope: "ticker" | "total", limit: number, ticker: string | null) => {
      const message =
        scope === "ticker" && ticker
          ? te(
              "{ticker} already has {limit} saved strategies. Remove one before saving another.",
              { ticker, limit: String(limit) },
            )
          : te(
              "You already have {limit} saved strategies. Remove one before saving another.",
              { limit: String(limit) },
            );
      return new SavedStrategyLimitError(message, scope, limit, ticker);
    },
    [te],
  );

  const reload = useCallback(
    () =>
      api
        .listSavedStrategies()
        .then(({ saved_strategies, limits: nextLimits }) => {
          setStrategies(saved_strategies);
          if (nextLimits) setLimits(nextLimits);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [],
  );

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    void reload();
  }, [authStatus, reload]);

  const create = useCallback(
    async (input: SavedStrategyInput): Promise<SavedStrategy> => {
      const userSaved = input.user_saved !== false;
      const normalizedTicker = input.ticker.toUpperCase();

      if (userSaved) {
        const saved = strategies.filter((strategy) => strategy.user_saved);
        const tickerCount = saved.filter(
          (strategy) => strategy.ticker.toUpperCase() === normalizedTicker,
        ).length;
        if (tickerCount >= limits.per_ticker) {
          throw limitError("ticker", limits.per_ticker, normalizedTicker);
        }
        if (saved.length >= limits.total) {
          throw limitError("total", limits.total, null);
        }
      }

      try {
        const { saved_strategy } = await api.createSavedStrategy(input);
        setStrategies((prev) => [saved_strategy, ...prev]);
        return saved_strategy;
      } catch (error) {
        const quota = savedStrategyQuotaErrorBody(error);
        if (!quota) throw error;
        throw limitError(quota.scope, quota.limit, quota.ticker);
      }
    },
    [limitError, limits, strategies],
  );

  const update = useCallback(
    async (
      id: string,
      patch: Partial<SavedStrategyInput>,
    ): Promise<SavedStrategy> => {
      const { saved_strategy } = await api.updateSavedStrategy(id, patch);
      setStrategies((prev) =>
        prev.map((s) => (s.id === id ? saved_strategy : s)),
      );
      return saved_strategy;
    },
    [],
  );

  const remove = useCallback(async (id: string) => {
    await api.deleteSavedStrategy(id);
    setStrategies((prev) => prev.filter((s) => s.id !== id));
  }, []);

  return useMemo(
    () => ({
      status,
      strategies,
      limits,
      error,
      reload,
      create,
      update,
      remove,
    }),
    [status, strategies, limits, error, reload, create, update, remove],
  );
}

function savedStrategyQuotaErrorBody(error: unknown): {
  scope: "ticker" | "total";
  limit: number;
  ticker: string | null;
} | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body as SavedStrategyQuotaErrorBody;
  if (
    body.error !== "saved_strategy_limit_reached" ||
    (body.scope !== "ticker" && body.scope !== "total") ||
    typeof body.limit !== "number"
  ) {
    return null;
  }
  return {
    scope: body.scope,
    limit: body.limit,
    ticker: body.ticker ?? null,
  };
}

export function SavedStrategiesProvider({ children }: { children: ReactNode }) {
  const value = useSavedStrategiesProvider();
  return (
    <SavedStrategiesContext.Provider value={value}>
      {children}
    </SavedStrategiesContext.Provider>
  );
}
